import { isDefined, isNotDefined } from '@sgme/fp';

import { isOrderFromProduct } from '@/store/orders/helpers.ts';
import type { AppState } from '@/store/store.ts';
import { createWeakSelector } from '@/store/storeMethods.ts';
import { tilesAdapterSelectors } from '@/store/tiles/adapter.ts';

const selectTileById = createWeakSelector(
  (state: AppState) => state.tiles,
  (_state: AppState, tileId: string) => tileId,
  (state, tileId) => tilesAdapterSelectors.selectById(state, tileId),
);

export const selectTileProductType = createWeakSelector(selectTileById, (tile) => tile?.productType);

export const selectTilesOfTab = createWeakSelector(
  (state) => state,
  (_, tabId: string) => tabId,
  (state: AppState, tabId: string) =>
    tilesAdapterSelectors.selectAll(state.tiles).filter((tile) => tile.tabId === tabId),
);

export const selectCashTileIdsOfTab = (state: AppState, tabId: string) => {
  const allTabTiles = selectTilesOfTab(state, tabId);

  return allTabTiles.filter((tile) => tile.productType === 'Cash').map((tile) => tile.tileId);
};

export const selectTileProduct = (tileId: string) => (state: AppState) => {
  const productType = selectTileProductType(state, tileId);
  switch (productType) {
    case 'Cash':
      return state.cash[tileId];
    case 'Swap':
      return state.swap[tileId];
    case 'Option':
      return state.option[tileId];
    case 'FlexiForward':
      return state.flexiForward[tileId];
    case 'TargetAccumulator':
      return state.targetAccumulator[tileId];
    case 'ForwardAccumulator':
      return state.forwardAccumulator[tileId];
    case 'SmartRfs':
      return undefined; // TODO : where is it ?
    case 'TakeProfitOrder':
    case 'CallOrder':
    case 'FalconOrder':
    case 'FixingOrder':
    case 'NightjarOrder':
    case 'StopLossOrder':
    case 'Twap+Order':
      return state.orders[tileId];
  }
};

/**
 * searches the default clientId that is applicable to a tile
 */
export const selectTabClientIdOfTile = createWeakSelector(
  (state: AppState, tileId: string) => state.tiles.entities[tileId]?.tabId,
  (state: AppState) => state.tabs,
  (tabId, tabs) => {
    if (isNotDefined(tabId)) {
      return tabs.defaultClientId;
    }

    const tab = tabs.tabs.entities[tabId];

    return tab?.clientId ?? tabs.defaultClientId;
  },
);

/**
 * searches the currencyPair that is applicable to a tile,
 */
export const selectTileCurrencyPair = createWeakSelector(
  (state: AppState, tileId: string) => selectTileProduct(tileId)(state),
  (product): string | undefined => {
    if (isNotDefined(product)) {
      return undefined;
    }
    return isOrderFromProduct(product)
      ? isDefined(product.properties.currencyPair?.currency1) && isDefined(product.properties.currencyPair?.currency2)
        ? `${product.properties.currencyPair?.currency1}/${product.properties.currencyPair?.currency2}`
        : undefined
      : product.properties.currencyPair;
  },
);

/**
 * searches the clientId that is applicable to a tile,
 * beginning on the linked product and falling back to the clientId of the tab
 */
export const selectTileClientId = createWeakSelector(
  (state: AppState, tileId: string) => selectTileProduct(tileId)(state),
  (state: AppState, tileId: string) => selectTabClientIdOfTile(state, tileId),
  (product, tabClientId): number => product?.clientId ?? tabClientId,
);

export const selectTileProductName = (tileId: string) => (state: AppState) => {
  const product = selectTileProduct(tileId)(state);
  return product?.type;
};
